import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from '../hooks/useSoundEffects';
import { getEffectiveSoundOutputVolume } from '../hooks/soundEffectsManager';
import { getTutorialVoicePath, stopTutorialVoice } from './tutorialVoiceMap';
import './TutorialMessageBox.css';

const PREFERENCE_FACTION_MAP = {
  aggressive: 'The Corrupted Core',
  greedy: 'The Covenant',
  patient: 'The Last Crusade',
  controlling: 'The Forsaken Pact',
  creative: 'The Shadow Consortium',
};

const FACTION_ORB_BACKGROUNDS = {
  'The Corrupted Core': 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)',
  'The Covenant': 'linear-gradient(to right, #F4A460 50%, #1A1A1A 50%)',
  'The Last Crusade': 'linear-gradient(to right, #F4A460 50%, #556B2F 50%)',
  'The Forsaken Pact': 'linear-gradient(to right, #556B2F 50%, #8B0000 50%)',
  'The Shadow Consortium': 'linear-gradient(to right, #1A1A1A 50%, #4B0082 50%)',
};

/**
 * Reusable tutorial message box with image, text, optional multichoice selector, and buttons.
 * @param {Object} props
 * @param {string} props.image - Path to image (e.g. /images/tutorial_welcome.png)
 * @param {string} props.text - Main text content
 * @param {string} [props.goldText] - Optional paragraph rendered in gold below the main text
 * @param {Array<{key: string, value: string}>} [props.choices] - Optional multichoice options
 * @param {string} [props.choiceLabelPrefix] - Translation key prefix for choice labels (e.g. 'battleOverview.tutorial'). Default: 'welcome.strategyPreference'
 * @param {Function} [props.onSelect] - Called when user selects a choice (for strategy selection)
 * @param {Function} props.onContinue - Called when user clicks Continue
 * @param {Function} [props.onCancel] - Called when user clicks Cancel
 * @param {boolean} [props.showCancel] - Whether to show Cancel button
 * @param {boolean} [props.disabled] - Disable choice buttons (e.g. while submitting)
 * @param {string} [props.voiceKey] - Translation key for tutorial voice (e.g. 'welcome.tutorial.welcome'). Voice stops when dialog is closed.
 * @param {string[]} [props.voiceKeys] - Optional array of voice keys to play in sequence in the same dialog (e.g. intro then gold). Overrides voiceKey when set.
 * @param {string} [props.wrapperClassName] - Optional extra class on the root `.tutorial-wrapper` (e.g. page-specific z-index).
 */
function TutorialMessageBox({ image, text, goldText, choices, choiceLabelPrefix = 'welcome.strategyPreference', onSelect, onContinue, onCancel, showCancel = false, disabled = false, voiceKey, voiceKeys, wrapperClassName }) {
  const { t } = useTranslation();
  const voiceAudioRef = useRef(null);
  const pathsToPlayRef = useRef([]);
  const nextIndexRef = useRef(0);

  useEffect(() => {
    const keys = Array.isArray(voiceKeys) && voiceKeys.length > 0
      ? voiceKeys
      : (voiceKey ? [voiceKey] : []);
    const paths = keys.map(k => getTutorialVoicePath(k)).filter(Boolean);
    stopTutorialVoice(voiceAudioRef);
    voiceAudioRef.current = null;
    pathsToPlayRef.current = paths;
    nextIndexRef.current = 0;

    if (paths.length === 0) return;

    function playNext() {
      if (nextIndexRef.current >= pathsToPlayRef.current.length) return;
      const volume = getEffectiveSoundOutputVolume(1);
      if (volume === null) return;
      const path = pathsToPlayRef.current[nextIndexRef.current];
      nextIndexRef.current += 1;
      try {
        const audio = new Audio(path);
        audio.volume = volume;
        voiceAudioRef.current = audio;
        audio.addEventListener('ended', () => {
          playNext();
        });
        audio.play().catch(() => {});
      } catch (_) {}
    }

    playNext();

    return () => {
      stopTutorialVoice(voiceAudioRef);
      voiceAudioRef.current = null;
      pathsToPlayRef.current = [];
    };
  }, [voiceKey, voiceKeys]);

  return (
    <div className={`tutorial-wrapper${wrapperClassName ? ` ${wrapperClassName}` : ''}`}>
      <div className="tutorial-message-box">
        {image && (
          <div className="tutorial-image-column">
            <div className="tutorial-image-container">
              <img src={image} alt="" className="tutorial-image" />
            </div>
          </div>
        )}
        <div className="tutorial-content-column">
        <div className="tutorial-text">
          {text}
          {goldText && <p className="tutorial-text-gold">{goldText}</p>}
        </div>
        {choices && choices.length > 0 && (
          <div className="tutorial-choices">
            {choices.map(({ key, value }) => {
              const factionName = PREFERENCE_FACTION_MAP[key];
              const orbBg = factionName ? FACTION_ORB_BACKGROUNDS[factionName] : null;
              return (
                <button
                  key={key}
                  className="tutorial-choice-button"
                  onClick={() => {
                    if (!disabled && onSelect) {
                      playSoundEffect('/sounds/click.mp3');
                      onSelect(value);
                    }
                  }}
                  disabled={disabled}
                >
                  {orbBg && (
                    <span
                      className="tutorial-choice-orb"
                      style={{ background: orbBg }}
                      title={factionName}
                    />
                  )}
                  {t(`${choiceLabelPrefix}.${key}`)}
                </button>
              );
            })}
          </div>
        )}
        <div className="tutorial-buttons">
          {!choices?.length && (
            <button
              className="tutorial-continue-button"
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                onContinue();
              }}
            >
              {t('welcome.tutorial.continue')}
            </button>
          )}
          {showCancel && (
            <button
              className="tutorial-cancel-button"
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                if (onCancel) onCancel();
              }}
            >
              {t('common.cancel')}
            </button>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

export default TutorialMessageBox;
